<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="bg-bleumarine text-white py-16 md:py-24">
      <div class="container mx-auto px-4">
        <HeroSlider />
      </div>
    </section>

    <!-- Services Overview -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <h2 class="section-title text-center">Nos Domaines d'Expertise</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="service in services" :key="service.title" class="card">
            <div class="w-12 h-12 rounded-lg flex items-center justify-center mb-4" :class="service.bgClass">
              <component :is="service.icon" class="w-6 h-6" style="color: #fff;" />
            </div>
            <h3 class="text-xl font-semibold mb-2">{{ service.title }}</h3>
            <p class="text-gray-600">{{ service.description }}</p>
          </div>
        </div>
        <div class="text-center mt-10">
          <router-link to="/services" class="btn-primary inline-block">
            Découvrir tous nos services
          </router-link>
        </div>
      </div>
    </section>

    <!-- About Preview -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto">
          <h2 class="section-title text-center">À Propos de TechsPartner</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <p class="text-gray-700 mb-4">
                Basée à Bingerville, TechsPartner est une entreprise ivoirienne spécialisée dans la transformation digitale. Nous accompagnons les entreprises publiques et privées en Afrique subsaharienne.
              </p>
              <p class="text-gray-700 mb-4">
                Notre mission est de fournir des solutions innovantes et adaptées aux besoins spécifiques de nos clients, tout en garantissant la sécurité et la performance de leurs infrastructures IT.
              </p>
              <router-link to="/about" class="text-primary-600 hover:text-primary-700 font-semibold inline-flex items-center">
                En savoir plus
                <ArrowRight class="w-5 h-5 ml-2" :stroke="'#fff'" />
              </router-link>
            </div>
            <div class="bg-turquoise rounded-lg p-8 text-white">
              <h3 class="text-2xl font-bold mb-6">Nos Chiffres Clés</h3>
              <div class="grid grid-cols-2 gap-4">
                <div class="text-center">
                  <div class="text-3xl font-bold mb-2">50+</div>
                  <div class="text-violet text-sm">Projets Réalisés</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-bold mb-2">30+</div>
                  <div class="text-violet text-sm">Clients Satisfaits</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-bold mb-2">10+</div>
                  <div class="text-violet text-sm">Experts Certifiés</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-bold mb-2">5+</div>
                  <div class="text-violet text-sm">Années d'Expérience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Latest Articles -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <h2 class="section-title text-center">Derniers Articles</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div v-for="article in articles" :key="article.id" class="card">
            <div class="bg-gray-200 h-48 rounded-lg mb-4 flex items-center justify-center">
              <span class="text-gray-400">{{ article.category }}</span>
            </div>
            <span class="text-sm text-primary-600 font-medium">{{ article.category }}</span>
            <h3 class="text-lg font-semibold mt-2 mb-2">{{ article.title }}</h3>
            <p class="text-gray-600 text-sm mb-4">{{ article.excerpt }}</p>
            <div class="flex items-center justify-between text-sm text-gray-500">
              <span>{{ article.date }}</span>
              <router-link to="/blog" class="text-primary-600 hover:text-primary-700">Lire plus →</router-link>
            </div>
          </div>
        </div>
        <div class="text-center mt-10">
          <router-link to="/blog" class="btn-primary inline-block">
            Voir tous les articles
          </router-link>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 bg-violet">
      <div class="container mx-auto px-4 text-center">
        <h2 class="text-2xl md:text-3xl font-bold text-white mb-4">
          Prêt à Transformer Votre Entreprise ?
        </h2>
        <p class="text-white mb-8 max-w-2xl mx-auto">
          Contactez-nous dès aujourd'hui pour discuter de vos projets et découvrir comment nous pouvons vous accompagner dans votre transformation digitale.
        </p>
        <router-link to="/contact" class="bg-white text-bleumarine hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition duration-300 inline-block">
          Nous Contacter
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import HeroSlider from '../components/HeroSlider.vue'
import { Server, Globe, Database, FileText, Wrench, GraduationCap, ArrowRight } from 'lucide-vue-next'

const services = ref([
  {
    icon: Server,
    bgClass: 'bg-bleumarine',
    title: 'Datacenter & Productivité',
    description: 'Energie, refroidissement, serveurs, virtualisation, cloud computing, stockage et sauvegarde.'
  },
  {
    icon: Globe,
    bgClass: 'bg-turquoise',
    title: 'Réseaux & Télécommunications',
    description: 'Fourniture et configuration d\'équipements, câblage, interconnexions, VoIP et sécurité réseau.'
  },
  {
    icon: Database,
    bgClass: 'bg-violet',
    title: 'Développement & Bases de données',
    description: 'ERP/PGI, applications métiers, développement sur mesure, web et mobile, bases de données sécurisées.'
  },
  {
    icon: FileText,
    bgClass: 'bg-bleumarine',
    title: 'Audit & Gouvernance IT',
    description: 'Audit SI, gouvernance, politique de sécurité, gestion de projets et assistance à maîtrise d\'ouvrage.'
  },
  {
    icon: Wrench,
    bgClass: 'bg-turquoise',
    title: 'Matériels & Sécurité électronique',
    description: 'Postes de travail, ordinateurs portables, vidéosurveillance, contrôle d\'accès, protection incendie.'
  },
  {
    icon: GraduationCap,
    bgClass: 'bg-violet',
    title: 'Formations Professionnelles',
    description: 'Formations certifiantes, bureautique, sécurité SI et formations personnalisées.'
  }
])

const articles = ref([
  {
    id: 1,
    title: 'Les meilleures pratiques de sécurité cloud en 2024',
    excerpt: 'Découvrez comment sécuriser vos infrastructures cloud avec les dernières recommandations...',
    category: 'Cloud',
    date: '15 Jan 2024'
  },
  {
    id: 2,
    title: 'Introduction à DevOps pour les entreprises',
    excerpt: 'Comprendre les principes DevOps et comment les appliquer dans votre organisation...',
    category: 'DevOps',
    date: '10 Jan 2024'
  },
  {
    id: 3,
    title: 'Cybersécurité : protéger votre entreprise contre les ransomwares',
    excerpt: 'Guide complet pour se prémunir contre les attaques par ransomware...',
    category: 'Cyber sécurité',
    date: '5 Jan 2024'
  }
])
</script>
